import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { httpBatchLink, loggerLink, TRPCClientError } from '@trpc/client';
import { clientConfig } from 'pos-client/clientConfig.ts';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import {
  extractProblemDetails,
  extractValidationErrorDetails,
  trpc,
} from 'pos-client/utils/trpc.ts';
import SuperJSON from 'superjson';

export const TrpcQueryProvider = ({ children }: { children: ReactNode }) => {
  const { t, i18n } = useTranslation();
  const { showError } = useNotifications();

  const handleTRPCError = (error: unknown) => {
    if (error instanceof TRPCClientError) {
      if (error.data?.code === 'FORBIDDEN') {
        showError(t('common.errors.unauthorisedAction'));
        return;
      } else if (error.data?.code === 'UNAUTHORIZED') {
        window.location.reload();
      }
    }

    // handle problem details error format
    const validationErrorDetails = extractValidationErrorDetails(error);

    if (validationErrorDetails) {
      const { status, errors } = validationErrorDetails;
      if (status) {
        const tKey = `common.errors.${status}`;
        if (i18n.exists(tKey)) {
          //@ts-ignore
          showError(t(tKey));
          return;
        } else {
          if (errors) {
            const messages = Object.entries(errors).map(
              ([key, value]) => `${key}: ${value}`
            );
            showError(messages.join('\n'));
          } else {
            showError('Unknown validation error occurred!');
          }
        }
      } else {
        showError(t('common.errors.genericError'));
      }
    }

    // handle problem details error format
    const problemDetails = extractProblemDetails(error);

    if (problemDetails) {
      const { errorCode, detail } = problemDetails;
      if (errorCode) {
        const tKey = `common.errors.${errorCode}`;
        if (i18n.exists(tKey)) {
          //@ts-ignore
          showError(t(tKey));
          return;
        } else {
          showError(detail);
        }
      } else {
        showError(t('common.errors.genericError'));
      }
    }
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false, refetchOnWindowFocus: false },
          mutations: { retry: false },
        },
        queryCache: new QueryCache({
          onError: handleTRPCError,
        }),
        mutationCache: new MutationCache({
          onError: handleTRPCError,
        }),
      })
  );

  const [trpcClient] = useState(() =>
    trpc.createClient({
      transformer: SuperJSON,
      links: [
        loggerLink({
          enabled: (opts) =>
            ((process.env.NODE_ENV === 'development' ||
              clientConfig.datadog.environment.toLowerCase() === 'int-test') &&
              typeof window !== 'undefined') ||
            (opts.direction === 'down' && opts.result instanceof Error),
        }),
        // @ts-ignore
        httpBatchLink({
          url: `${clientConfig.bffBaseUrl}/api/trpc`,
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
              mode: 'cors',
            });
          },
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </trpc.Provider>
  );
};
